/**
 * Tooltip component displays data values at hover position.
 * Subscribes to 'hover' event only for high-frequency updates.
 *
 * Uses core/hoverUtils for pure hover event building logic.
 * This component is a thin visual wrapper.
 */

import React from "react";

import styles from "../styles/styles.module.css";
import { useChartContext } from "../core/ChartContext";
import { buildHoverEvent, calculateTooltipPosition } from "~utils/ticker-chart";
import { useChartSubscription } from "~hooks/ticker-chart";
import { type HoverEvent } from "~types/types";

export interface TooltipProps {
  /**
   * Callback to render custom tooltip content.
   * Receives the hover event and should return a React node.
   * Return null or undefined to hide the tooltip.
   */
  renderTooltip: (event: HoverEvent) => React.ReactNode;
  /** Offset from cursor in pixels */
  offsetX?: number;
  /** Offset from cursor in pixels */
  offsetY?: number;
}

/**
 * Tooltip displays data values for hovered point.
 * Updates at 60fps without re-rendering other components.
 * Only renders if renderTooltip callback is provided and returns content.
 */
const TooltipInner: React.FC<TooltipProps> = ({
  renderTooltip,
  offsetX = 10,
  offsetY = 10,
}) => {
  const store = useChartContext();

  // Subscribe to hover only - scales/viewport are read synchronously from store
  useChartSubscription(store, "hover");

  const hover = store.getHoverState();
  const state = store.getState();
  const layout = store.getLayout();
  const scales = store.getScales();
  const viewport = store.getViewport();

  if (!hover.visible || hover.nearestPoints.size === 0) {
    return null;
  }

  // Get the first data point to use its timestamp
  const firstSeriesId = Array.from(hover.nearestPoints.keys())[0];
  const firstPoint = firstSeriesId
    ? hover.nearestPoints.get(firstSeriesId)
    : null;

  if (!firstPoint) {
    return null;
  }

  // Use the actual data point's timestamp (not cursor timestamp)
  const dataPointTimestamp = firstPoint.timestamp;

  // Check if the data point is still within the visible viewport
  if (
    dataPointTimestamp < viewport.start ||
    dataPointTimestamp > viewport.end
  ) {
    return null;
  }

  // Build the hover event for the callback
  const hoverEvent = buildHoverEvent(
    state.mode,
    hover,
    state.seriesMeta,
    dataPointTimestamp
  );

  if (!hoverEvent) {
    return null;
  }

  // Call the renderTooltip callback to get the content
  const tooltipContent = renderTooltip(hoverEvent);

  // Don't render if callback returns null/undefined
  if (tooltipContent === null || tooltipContent === undefined) {
    return null;
  }

  // Use the data point's X position (same as crosshair vertical line)
  const snappedX = scales.x.scale(dataPointTimestamp);

  // Estimate tooltip size (can be adjusted based on content)
  const tooltipWidth = 200;
  const tooltipHeight = 120;

  // Calculate tooltip position using core function
  const { x, y } = calculateTooltipPosition({
    snappedX,
    chartLeft: layout.priceArea.x,
    chartRight: layout.priceArea.x + layout.priceArea.width,
    chartTop: layout.priceArea.y,
    chartHeight: layout.priceArea.height,
    tooltipWidth,
    tooltipHeight,
    offsetX,
    offsetY,
  });

  return (
    <foreignObject
      x={x}
      y={y}
      width={tooltipWidth}
      height={tooltipHeight}
      className={styles.tooltipContainer}
      pointerEvents="none"
      style={{ overflow: "visible" }}
    >
      {tooltipContent}
    </foreignObject>
  );
};

export const Tooltip = React.memo(TooltipInner);
Tooltip.displayName = "Tooltip";
